'use client';
import { ReactNode, useCallback, useState, useEffect, createContext, useContext, useRef } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import Script from 'next/script';
import React from 'react';

import { Table } from '@/types';
import { getCookie } from 'cookies-next';
import { useAccountsList, useUpdateUserProfile } from '@/utils/react-query-hooks';
import { UserContext } from '../AppProviders';
import { DEMO_USER_EMAIL } from '@/constants';

import Anchor from 'shared-ui/components/Anchor';
import { Bars3Icon } from '@heroicons/react/24/outline'
import { MegaphoneIcon } from '@heroicons/react/24/solid';
import { Sidebar } from './components/Sidebar';
import { Logo } from 'src/components/landing/navigation/Logo';
import AccountBadge from 'src/components/presentational/tailwind/AccountBadge';
import { classNames } from 'src/lib/utils';

// User profile context
export const UserProfileContext = createContext(null);
// Accounts context
export const AccountsContext = createContext(null);

export function KeesyClientLayout({
  children,
  hasUpgradableAccounts,
  userProfile: initialUserProfile,
  instagramAccounts,
  email,
}: {
  children: ReactNode;
  hasUpgradableAccounts: boolean;
  userProfile: Table<'user_profiles'>;
  instagramAccounts: Table<'accounts'>[];
  email: string;
}) {

  const router = useRouter();
  const pathName = usePathname();
  const isBrowser = typeof window !== 'undefined';
  const [isDemoSubdomain, setIsDemoSubdomain] = useState(isBrowser && window.location.hostname.split('.')[0] === 'demo');
  const [isOnAccountsPage, setIsOnAccountsPage] = useState(isBrowser && window.location.pathname === '/app/accounts');
  const [isOnAdminPage, setIsOnAdminPage] = useState(isBrowser && window.location.pathname.includes('/app/app_admin'));
  const [isOnBoostPage, setIsOnBoostPage] = useState(isBrowser && window.location.pathname.includes('/boost'));
  const [isOnSetupPage, setIsOnSetupPage] = useState(isBrowser && window.location.pathname.includes('/start'));
  const [pageHeaderTitle, setPageHeaderTitle] = useState('Dashboard');
  useEffect(() => {
    setIsDemoSubdomain(isBrowser && window.location.hostname.split('.')[0] === 'demo');
    setIsOnAccountsPage(isBrowser && window.location.pathname === '/app/accounts');
    setIsOnBoostPage(isBrowser && window.location.pathname.includes('/boost'));
    setIsOnSetupPage(isBrowser && window.location.pathname.includes('/start'));
    // console.log("[ClientLayout.tsx] isDemoSubdomain: ", isDemoSubdomain)
    // console.log("[ClientLayout.tsx] isOnAccountsPage: ", isOnAccountsPage)
    // console.log("[ClientLayout.tsx] isOnBoostPage: ", isOnBoostPage)
    // console.log("[ClientLayout.tsx] isOnSetupPage: ", isOnSetupPage)

    if (isBrowser && window.location.pathname === '/app/accounts') {
      setPageHeaderTitle('Accounts');
    } else if (isOnAdminPage) {
      setPageHeaderTitle('Admin');
    } else if (window.location.pathname === '/app/boost') {
      setPageHeaderTitle('Boost');
    } else if (window.location.pathname.includes('/settings') && window.location.pathname.includes('/account')) {
      setPageHeaderTitle('Growth Settings');
    } else if (window.location.pathname === '/app/start') {
      setPageHeaderTitle('Setup');
    } else if (window.location.pathname === '/app/settings') {
      setPageHeaderTitle('Account Settings');
    } else {
      setPageHeaderTitle('Dashboard');
    }

  }, [router, pathName]);

  const [instagramAccountsList, setInstagramAccountsList] = useState(instagramAccounts);
  const { data: allInstagramAccounts, isLoading: isLoadingAllInstagramAccounts } = useAccountsList(instagramAccounts);
  useEffect(() => {
    if (allInstagramAccounts) {
      setInstagramAccountsList(allInstagramAccounts);
    }
  }, [allInstagramAccounts]);

  const [selectedAccount, setSelectedAccount] = useState(null);
  useEffect(() => {
    if (!pathName.includes('/app/account/')) return;
    const accountId = pathName.split("/")[3];
    const account = instagramAccountsList.find(acc => acc.id === accountId);
    if (!account) {
      router.push('/app');
    } else {
      setSelectedAccount(account);
    }
  }, [pathName, instagramAccountsList, router]);


  const searchParams = useSearchParams()
  const setSearchParam = useCallback((key: string, value: string) => {
    if (!isBrowser) return;
    if (key === 'step' && value === '0') {
      router.push(`/app/start`, { scroll: false });
      return;
    }
    const params = new URLSearchParams(window.location.search);
    params.set(key, value);
    console.log("[ClientLayout.tsx] Setting search params: ", params.toString())
    router.push(`?${params.toString()}`, { scroll: false });
  }, [router]);

  const user = useContext(UserContext)
  const { mutate: updateUserProfile, isLoading, error } = useUpdateUserProfile({});
  useEffect(() => {
    const toltReferralId = getCookie('tolt_referral');
    if (toltReferralId && typeof toltReferralId === 'string' && !initialUserProfile.traffic_source) {
      updateUserProfile({ traffic_source: toltReferralId }, {
        onError: (error) => console.error('Error updating tolt_referral_id:', error)
      });
    }
    const gclid = getCookie('gclid');
    if (gclid && typeof gclid === 'string' && !initialUserProfile.traffic_source) {
      updateUserProfile({ traffic_source: gclid }, {
        onError: (error) => console.error('Error updating traffic_source:', error)
      });
    }
    if (initialUserProfile && !initialUserProfile.user_info) {
      // Fetch user info from /api/user_info and update user profile
      fetch('/api/user_info')
        .then(response => response.json())
        .then(data => {
          if (data) {
            updateUserProfile({ user_info: data }, {
              onError: (error) => console.error('Error updating user_info:', error)
            });
          } else {
            console.error('Incomplete user_info:', data);
          }
        })
        .catch(error => console.error('Error fetching user_info:', error));
    }
    if (!initialUserProfile.full_name && initialUserProfile.customer_id && initialUserProfile.gateway) {
      // Fetch customer name from Chargebee and update user profile
      fetch(`/api/chargebee/get-customer-name?site=${initialUserProfile.gateway}&customerId=${initialUserProfile.customer_id}`)
        .then(response => response.json())
        .then(data => {
          const chargebeeCustomerName = data.name;
          if (chargebeeCustomerName && typeof chargebeeCustomerName === 'string' && chargebeeCustomerName !== initialUserProfile.full_name) {
            updateUserProfile({ full_name: chargebeeCustomerName }, {
              onError: (error) => console.error('Error updating user name:', error)
            });
          }
        })
        .catch(error => console.error('Error fetching customer name:', error));
    }
    if (!initialUserProfile.email && user.email && typeof user.email === 'string' && user.email !== DEMO_USER_EMAIL) {
      updateUserProfile({ email: user.email }, {
        onError: (error) => console.error('Error updating user email:', error)
      });
    }
  }, [initialUserProfile, updateUserProfile]);

  // const [shouldShowLinkInBioCard, setShouldShowLinkInBioCard] = useState(false);
  // useEffect(() => {
  //   // Check if "dismiss-link-in-bio" cookie is set
  //   const dismissLinkInBio = getCookie('dismiss-link-in-bio');
  //   if (!dismissLinkInBio && dismissLinkInBio != 'true')
  //     setShouldShowLinkInBioCard(true);
  // }, []);

  // const [hasHighlightIdentifiedUser, setHasHighlightIdentifiedUser] = useState(() => {
  //   if (typeof window !== 'undefined')
  //     // console.log("[ClientLayout.tsx] window.sessionStorage.getItem('highlightIdentifier'): ", window.sessionStorage.getItem('highlightIdentifier'))
  //     return window.sessionStorage.getItem('highlightIdentifier') !== null;
  //   return true;
  // });

  /* Set Intercom user data */
  // const { account, isLoading: isFetchingAccount, error: accountError } = useAccountContext()
  // const userContext = useMemo(() => ({
  //   name: user.name,
  //   email: user.email,
  //   // instagram_profile: account ? {
  //   //   username: account.username,
  //   //   full_name: account.name,
  //   //   followers: account.followers,
  //   //   following: account.followings,
  //   //   upgrow_account_type: account.account_type,
  //   //   upgrow_starting_followers: account.starting_followers,
  //   //   upgrow_active: account.active,
  //   //   upgrow_settings: account.targeting_options,
  //   // } : {},
  // }), [user])
  useEffect(() => {
    if (typeof window === 'undefined') return;
    console.log(`[ClientLayout.tsx] Setting Intercom user data for ${JSON.stringify(user)}`);

    if (typeof window !== 'undefined' && window.intercomSettings !== undefined && window.intercomSettings.user_id === user.id)
      console.log("window.intercomSettings is already set");

    (window as any).intercomSettings = (user.email === DEMO_USER_EMAIL)
      ? {
        api_base: "https://api-iam.intercom.io",
        app_id: "g6b4q3en"
      }
      : {
        app_id: 'g6b4q3en',
        background_color: '#fa75e3',
        user_id: user.id,
        email: user.email,
        //...(initialUserProfile.full_name && { name: initialUserProfile.full_name }),
        // is_customer: (accounts && accounts.length && accounts.filter(ac=> ac=='premium'||ac=='turbo'||ac=='experts').length),
        // avatar: {
        //     "type": "avatar", 
        //     "image_url": user.picture
        // },
        created_at: user.created_at,
        // timezone: timezone,
        // downgraded:false,
        // has_accounts: (accounts && accounts.length > 0),
        // companies: accounts.map(account => {
        //     const ac = {};
        //     ac["company_id"] = account.id;
        //     ac["created_at"] = account.created_at;
        //     ac["name"] = account.username;
        //     ac["plan"] = account.account_type;
        //     ac["size"] = account.followers;
        //     ac["followers"] = account.followers;
        //     ac["active"] = account.active==true?true:false;
        //     ac["experts_enabled"] = account.experts_enabled==true?true:false;
        //     ac["website"] = "https://instagram.com/"+account.username;
        //     return ac;
        // })
      }
  }, [user])

  const [sidebarOpen, setSidebarOpen] = useState(false)
  // Hack to scroll page content while cursor is not over it
  const pageContentRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleWheel = event => {
      const hoveredElement = document.elementFromPoint(event.clientX, event.clientY);
      if (!pageContentRef.current || hoveredElement === pageContentRef.current || pageContentRef.current.contains(hoveredElement)) return;
      if (pageContentRef.current.scrollHeight > pageContentRef.current.clientHeight) {
        event.preventDefault();
        pageContentRef.current.scrollBy({ top: event.deltaY });
      }
    };
    mainRef.current?.addEventListener('wheel', handleWheel, { passive: false });
    return () => mainRef.current?.removeEventListener('wheel', handleWheel);
  }, []);

  // Adjust the height of the main element for Safari on iOS
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const main = document.querySelector('main');
    if (main && window.innerWidth <= 768) { // Check if the device width is less than or equal to 768px (common breakpoint for mobile devices)
      const isSafari = window.navigator.userAgent.includes('Safari') && !window.navigator.userAgent.includes('Chrome');
      const isIOS = /iPad|iPhone|iPod/.test(window.navigator.userAgent);
      if (isSafari && isIOS) {
        main.style.height = 'calc(100vh - 180px)';
      } else {
        main.style.height = 'calc(100vh - 65px)';
      }
    }
  }, []);

  return (
    <>
      {/* <Suspense>
        <PostHogPageview />
      </Suspense>
      <PostHogProvider client={PostHog}> */}

      <UserProfileContext.Provider value={initialUserProfile}>
        <AccountsContext.Provider value={instagramAccountsList}>
          {/* {!hasHighlightIdentifiedUser && (
          <HighlightIdentify email={email} />
        )} */}

          {/* Debugging */}
          {/* {user && account && userContext && (
          <>
            <p>initialUserProfile: {JSON.stringify(initialUserProfile)}</p>
            <p>User: {JSON.stringify(user)}</p>
            <p>Account: {JSON.stringify(account)}</p>
            <p>UserContext: {JSON.stringify(userContext)}</p>
          </>
        )} */}


          {/* Keesy Layout */}

          <div className="fixed w-screen overflow-hidden lg:relative" style={{
            background: "#fdfbfe",
            height: '-webkit-fill-available'
          }}>

            {/* Light beam top left (in sidebar) */}
            <div className="absolute w-[433px] h-[1116px] top-[-216px] left-[-161px]">
              <div className="w-[324px] h-[324px] top-0 left-0 rounded-[162px] [background:linear-gradient(180deg,rgb(117,73,244)_0%,rgb(252,166,233)_100%)] saturate-150 absolute blur-[90px] opacity-[0.32]" />
            </div>
            {/* Light beam top right */}
            <div className="absolute w-[50vw] right-0 top-[-25vh] h-[50vh]">
              <div className="absolute w-full h-full top-0 right-[-30vw] rounded-[500px] blur-[64px] mix-blend-hard-light" style={{ background: 'var(--Gradients-Primary-Primary-Button, linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0.00) 100%), linear-gradient(135deg, #7549F4 2.88%, #FCA6E9 100%))' }}></div>
            </div>
            {/* Light beam bottom right */}
            <div className="absolute w-[433px] right-0 bottom-0 h-[50vh]">
              <div className="w-full h-full bottom-[-25vh] right-0 rounded-[400px] [background:linear-gradient(180deg,rgb(244.68,81.72,120.43)_0%,rgb(252.13,245.2,196.67)_100%)] absolute blur-[128px] opacity-[0.32]"></div>
            </div>

            <div className="hidden h-screen lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
              <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} accounts={instagramAccountsList} />
            </div>

            <div className="h-screen lg:pl-72">

              {/* Mobile navbar */}
              <div className="sticky top-0 z-40 lg:hidden">
                <div className="flex items-center justify-between h-16 px-4 bg-white border-b border-gray-200 shadow-sm gap-x-4 sm:gap-x-6 sm:px-6 lg:px-0 lg:shadow-none">
                  <Logo className="ml-1" />
                  <div className="flex items-center gap-3 ml-auto">
                    {/* Separator */}
                    <div className="w-px h-6 bg-gray-200" aria-hidden="true" />
                    <button className="text-gray-700" type="button" onClick={() => setSidebarOpen(true)}>
                      <span className="sr-only">Open sidebar</span>
                      <Bars3Icon className="w-6 h-6" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>

              <main ref={mainRef} className="relative h-screen py-3 pl-2 px-2 lg:!px-0 lg:!pr-3 lg:max-h-screen">
                <div ref={pageContentRef} id="pageContent" className={classNames("relative w-full h-full px-6 py-6 mx-auto overflow-scroll bg-white lg:max-h-full rounded-2xl",
                  isOnAdminPage ? 'max-w-7xl' : 'max-w-6xl'
                )} style={{ boxShadow: '0px 0px 0px 1px rgba(47, 10, 154, 0.08), 0px 1px 2px 0px rgba(47, 10, 154, 0.10)' }}>
                  <div className="inline-flex flex-col items-start justify-start w-full gap-5 bg-white">
                    <div className="inline-flex items-center self-stretch justify-start gap-4">
                      <div className="text-lg font-medium leading-normal grow shrink basis-0 text-zinc-900">{pageHeaderTitle}</div>
                      {selectedAccount && (
                        <AccountBadge accountType={selectedAccount.account_type} />
                      )}
                    </div>
                    <SeparatorLine className='mb-6' />
                  </div>

                  {children}

                </div>
              </main>
            </div>
          </div>

          {/* Demo banner at the bottom */}
          {isDemoSubdomain && (
            <div className="fixed inset-x-0 bottom-0" style={{ zIndex: 99999999 }}>
              <div className="bg-indigo-600">
                <div className="max-w-6xl px-3 py-3 mx-auto sm:px-6 lg:px-8">
                  <div className="flex flex-wrap items-center justify-between">
                    <div className="flex items-center flex-1 w-0">
                      <span className="flex p-2 bg-indigo-800 rounded-lg">
                        <MegaphoneIcon className="w-6 h-6 text-white" aria-hidden="true" />
                      </span>
                      <p className="ml-3 font-medium text-white truncate">
                        <span className="font-semibold md:hidden">Live Demo</span>
                        <span className="hidden md:inline">
                          <span className="font-semibold">Live Demo</span>
                          <span className="hidden sm:inline"> - Try Keesy for free!</span>
                        </span>
                      </p>
                    </div>
                    <div className="flex-shrink-0 order-3 w-full mt-2 sm:order-2 sm:mt-0 sm:w-auto">
                      <Anchor className="flex items-center justify-center px-4 py-2 text-sm font-medium text-indigo-600 bg-white border border-transparent rounded-md shadow-sm hover:bg-indigo-50"
                        href="https://www.keesy.com/start">
                        Start for free
                      </Anchor>
                    </div>
                    {/* <div className="flex-shrink-0 order-2 sm:order-3 sm:ml-3">
                        <button
                          type="button"
                          className="flex p-2 -mr-1 rounded-md hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-white sm:-mr-2"
                        >
                          <span className="sr-only">Dismiss</span>
                          <XMarkIcon className="w-6 h-6 text-white" aria-hidden="true" />
                        </button>
                      </div> */}
                  </div>
                </div>
              </div>
            </div>
          )}



          {/* Link in Bio card */}
          {/* {shouldShowLinkInBioCard && (
          <div className={`card animate-fade-in font-dashboard border border-gray-100 overflow-hidden p-0 fixed z-[2147483004] transform transition-transform 
            w-[calc(100%-32px)] bottom-[16px] left-[16px] right-[16px] translate-y-[63%] duration-500 hover:transform-none
            lg:w-80 lg:right-0 lg:left-auto lg:bottom-[135px] lg:translate-x-[240px] lg:translate-y-[10px] lg:-rotate-[15deg] lg:hover:rotate-0 lg:hover:translate-x-[-25px] lg:duration-300`}>
            <div className="relative overflow-hidden">
              <Image src="/img/graphics/upgrow-link-in-bio.gif" className="w-full" loading='lazy' width={320} height={184} alt="Free Link in Bio for Instagram from Keesy" />
              <div className="p-6 bg-white">
                <h2 className="text-xl font-bold tracking-tight text-gray-800">
                  NEW: Link in Bio that gets you more followers ✨
                </h2>
                <p className="mt-3 text-gray-800">
                  Simply replace your Instagram bio link and auto-magically get up to <b>350% more</b> followers & engagement
                </p>
                <div className="flex flex-row items-center gap-2 mt-6">
                  <Button type="button"
                    className="relative flex justify-center w-auto px-5 py-3 overflow-hidden font-medium text-white border border-transparent shadow-md text-md hover:shadow-lg focus:outline-none focus:ring-2"
                    onClick={() => {
                      // Maybe popup?
                      console.log('TODO: Implement Link in Bio')
                    }}
                  >
                    Try it for free!
                  </Button>
                  <button className="px-4 py-2 font-medium text-gray-600 transition-colors bg-transparent hover:text-gray-700"
                    onClick={() => {
                      setShouldShowLinkInBioCard(false);
                      document.cookie = "dismiss-link-in-bio=true; max-age=31536000; path=/";x
                    }}
                  >
                    No, thanks
                  </button>
                </div>
              </div>
            </div>
          </div>
        )} */}

          {/* Intercom for landing pages */}
          <Script id="intercom" strategy='lazyOnload'>{`(function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/g6b4q3en';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();`}</Script>

          {/* Tolt.io Affiliate Script */}
          {/* <Script async src="https://cdn.tolt.io/tolt.js" data-tolt="10ea0fb6-078b-40a3-991e-b6240af65cf8" strategy='lazyOnload'></Script> */}
        </AccountsContext.Provider>
      </UserProfileContext.Provider>
      {/* </PostHogProvider> */}
    </>
  );
}


export const SeparatorLine = React.memo<{ className?: string }>(({ className = '' }) => (
  <svg className={className} width="100%" height="1" viewBox="0 0 1104 1" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line y1="0.5" x2="100%" y2="0.5" stroke="#E1E2E4" strokeDasharray="3 3" />
  </svg>
));
